'use client';

import { useQuery } from '@rocicorp/zero/react';
import { useMemo } from 'react';
import {
  orderAllergenKeys,
  parseAllergenKeys,
  parseCustomAllergens,
  type CustomAllergen
} from '../../allergy-matrix/allergens';
import { queries } from '../../zero/queries';

export type ActiveAllergens = {
  /** Allergen keys per recipe id, from every row linked to the recipe. */
  byRecipe: Map<string, string[]>;
  /** Notes per recipe id ("Pork gelatin, not vegetarian"). */
  notesByRecipe: Map<string, string[]>;
  customAllergens: CustomAllergen[];
  matrixId: string | null;
  loaded: boolean;
};

/**
 * Dish allergens as the organization's active allergy matrix lists them.
 * Read live, never copied onto recipes, so menus and recipe pages change
 * the moment the matrix is edited or another matrix becomes active.
 */
export function useActiveAllergens(organizationId: string): ActiveAllergens {
  const [matrix, result] = useQuery(
    queries.allergyMatrices.active({ organizationId })
  );
  return useMemo(() => {
    const customAllergens = parseCustomAllergens(matrix?.customAllergens);
    const keys = new Map<string, Set<string>>();
    const notes = new Map<string, string[]>();
    for (const row of matrix?.rows ?? []) {
      if (!row.recipeId) {
        continue;
      }
      const set = keys.get(row.recipeId) ?? new Set<string>();
      parseAllergenKeys(row.allergenKeys).forEach(key => set.add(key));
      keys.set(row.recipeId, set);
      if (row.note?.trim()) {
        notes.set(row.recipeId, [
          ...(notes.get(row.recipeId) ?? []),
          row.note.trim()
        ]);
      }
    }
    return {
      byRecipe: new Map(
        [...keys].map(([recipeId, set]) => [
          recipeId,
          orderAllergenKeys(set, customAllergens)
        ])
      ),
      notesByRecipe: notes,
      customAllergens,
      matrixId: matrix?.id ?? null,
      loaded: result.type === 'complete'
    };
  }, [matrix, result.type]);
}
